import Vue from 'vue';
import moment from 'moment';
import store from '@/store';

const { SYSTEM_MESSAGES } = require('@/common/constants/');

const t = {
  get account() {
    return store.state.loggedAccount.account;
  },
  get socket() {
    return Vue.prototype.$sockets.chat;
  },
};

export class Chat {
  constructor(chat) {
    this.messages = [];
    this.loading = false;
    this.disabled = false;
    this.init(chat);
  }

  init(chat) {
    // Vue.prototype.$api.messages.markAsSeen(chat.id).then(() => this.update());
    // if (!chat.lastMessageSeen) Vue.prototype.$api.messages.markAsSeen(chat.id).then(() => this.update())
    if (t.account.role === 'PATIENT') this.disabled = chat.disabledForPatient && !chat.approved;

    Object.defineProperty(this, 'chat', { value: chat, writable: true });
    Object.defineProperty(this, 'queryParams', { value: null, writable: true });
    Object.defineProperty(this, 'listeners', {
      value: {
        onLeaveEvent: this.onLeaveEvent.bind(this),
        onMessageEvent: this.onMessageEvent.bind(this),
        onAcceptedEvent: this.onAcceptedEvent.bind(this),
        onRejectedEvent: this.onRejectedEvent.bind(this),
        onExceptionEvent: this.onExceptionEvent.bind(this),
        onDisablePatientEvent: this.onDisablePatientEvent.bind(this),
        onRemovedClinicAdminEvent: this.onRemovedClinicAdminEvent.bind(this),
      },
      enumerable: false,
    });

    if (!t.socket.connected) t.socket.connect();
    // Emit join-room message if chat is not new, as in that case chat has account id instead of room id
    if (!chat.isNew) t.socket.emit('join-room', { roomId: this.chat.id });
    t.socket.on('message', this.listeners.onMessageEvent);
    t.socket.on('exception', this.listeners.onExceptionEvent);

    if (t.account.role !== 'CLINIC_ADMIN') {
      t.socket.on('accepted', this.listeners.onAcceptedEvent);
      t.socket.on('rejected', this.listeners.onRejectedEvent);
      t.socket.on('disable-patient', this.listeners.onDisablePatientEvent);
      t.socket.on('removed-clinic-admin', this.listeners.onRemovedClinicAdminEvent);
    } else t.socket.on('leave', this.listeners.onLeaveEvent);
  }

  off() {
    t.socket.off('leave', this.listeners.onLeaveEvent);
    t.socket.off('message', this.listeners.onMessageEvent);
    t.socket.off('accepted', this.listeners.onAcceptedEvent);
    t.socket.off('rejected', this.listeners.onRejectedEvent);
    t.socket.off('exception', this.listeners.onExceptionEvent);
    t.socket.off('disable-patient', this.listeners.onDisablePatientEvent);
    t.socket.off('removed-clinic-admin', this.listeners.onRemovedClinicAdminEvent);
    t.socket.emit('leave', { roomId: this.chat.id });
  }

  createChat(initMessage) {
    console.log('createChat', initMessage);
    return Vue.prototype.$api.messages.createChat(t.account.clinicIds[0], { userId: this.chat.id, initMessage })
      .then(({ data }) => Promise.allSettled([
        store.dispatch('messenger/getChats'),
        store.dispatch('messenger/updateActiveChat', data.payload),
      ])).catch(console.dir);
  }

  getMessages(queryParams) {
    this.queryParams = queryParams;
    return Vue.prototype.$api.messages.getMessages(this.chat.id, queryParams).then(({ data: res }) => {
      console.log('Chat: get messages');
      res.payload.results.reverse().forEach(message => this.addMessage(message, 'simple'));
      return res.payload.total;
    });
  }

  async sendMessage(newMessage) {
    if (this.disabled) return;

    if (this.chat.isNew) {
      console.log('SendMessage: is new');
      await this.createChat(newMessage);
    } else if (this.chat.disabledForPatient && t.account.role === 'PATIENT') {
      console.log('SendMessage: send request for approve');
      await this.sendMessageForAccept(newMessage);
    } else {
      console.log('SendMessage: simple');
      t.socket.emit('message', { roomId: this.chat.id, text: newMessage });
    }
  }

  sendMessageForAccept(initMessage) {
    console.log('sendMessageForAccept', initMessage);
    return Vue.prototype.$api.messages.reopenChat(this.chat.id, { initMessage }).then(() => {
      t.socket.emit('leave', { roomId: this.chat.id });
      t.socket.emit('join-room', { roomId: this.chat.id });
      this.update();
      this.messages = [];
      return this.getMessages(this.queryParams);
    }).catch(console.dirinitMessage);
  }

  addMessage(message, type = 'simple') {
    message.type = type;
    message.isSystem = type === 'system';
    message.isOwner = message.ownerId === t.account.id;
    message.showAvatar = t.account.role === 'CLINIC_ADMIN';
    message.date = +moment(message.createdAt || moment().toISOString());

    const groupDate = +moment(message.createdAt || +moment()).startOf('day');

    if (this.messages.length) {
      const groupIndex = this.messages.findIndex(message => +message.groupDate === groupDate);

      if (~groupIndex) {
        const messages = this.messages[groupIndex].messages;
        if (message.date > messages[messages.length - 1].date) messages.push(message);
        else if (message.date < messages[0].date) messages.unshift(message);
        else {
          let index;
          for (let i = 0; i < messages.length - 1; i++) {
            if (message.date > messages[i].date && message.date < messages[i + 1].date) {
              index = i;
              break;
            }
          }
          messages.splice(index + 1, 0, message);
        }
      } else if (groupDate > this.messages[this.messages.length - 1].groupDate) this.messages.push({ groupDate, messages: [message] });
      else if (groupDate < this.messages[0].groupDate) this.messages.unshift({ groupDate, messages: [message] });
      else {
        let index;
        for (let i = 0; i < this.messages.length - 1; i++) {
          if (groupDate > this.messages[i].groupDate && groupDate < this.messages[i + 1].groupDate) {
            index = i;
            break;
          }
        }
        this.messages.splice(index + 1, 0, { groupDate, messages: [message] });
      }
    } else {
      this.messages.push({
        groupDate,
        messages: [message],
      });
    }
  }

  removeMessage(type = 'system') {
    if (this.messages.length) {
      const lastGroup = this.messages[this.messages.length - 1];
      const msgId = lastGroup.messages.findIndex(msg => msg.type === type);
      if (~msgId) {
        if (lastGroup.messages.length === 1) this.messages.pop();
        else lastGroup.messages.splice(msgId, 1);
      }
    }
  }

  update(isReset) {
    store.dispatch('messenger/getChats');
    if (isReset) store.dispatch('messenger/setActiveChat', null);
    else {
      store.dispatch('messenger/updateActiveChat', this.chat.id).then(chat => {
        console.log('Chat:', chat);
        if (t.account.role === 'PATIENT') this.disabled = chat.disabledForPatient && !chat.approved;
        this.chat = chat;
      });
    }
  }

  // Events:
  // On exception event handle. Remove disable message and add new exception message
  onExceptionEvent(error) {
    console.log('Chat: user exception', error);
    if (error.status === SYSTEM_MESSAGES.PATIENT_WAS_REJECTED) {
      this.removeMessage('system');
      if (t.account.role === 'PATIENT') this.addMessage(SYSTEM_MESSAGES.SYSTEM_MESSAGE_REJECTED, 'system');
      else if (t.account.role === 'CLINIC_ADMIN') this.addMessage(SYSTEM_MESSAGES.CA_SYSTEM_MESSAGE_REJECTED, 'system');
    } else if (error.status === SYSTEM_MESSAGES.ROOM_NOT_APPROVED) {
      this.removeMessage('system');
      if (t.account.role === 'PATIENT') this.addMessage(SYSTEM_MESSAGES.SYSTEM_MESSAGE, 'system');
      else if (t.account.role === 'CLINIC_ADMIN') this.addMessage(SYSTEM_MESSAGES.CA_SYSTEM_MESSAGE, 'system');
    } else if (error.status === SYSTEM_MESSAGES.USER_NOT_EXISTS_IN_THE_ROOM) {
      this.removeMessage('system');
      if (t.account.role === 'PATIENT') this.addMessage(SYSTEM_MESSAGES.SYSTEM_MESSAGE, 'system');
      else if (t.account.role === 'CLINIC_ADMIN') this.addMessage(SYSTEM_MESSAGES.CA_SYSTEM_MESSAGE, 'system');
    } else if (error.status === SYSTEM_MESSAGES.ROOM_DISABLED_FOR_PATIENT && t.account.role === 'PATIENT') {
      this.removeMessage('system');
      this.addMessage(SYSTEM_MESSAGES.SYSTEM_MESSAGE_DISABLED, 'system');
    }
  }

  // On message event.
  // Add new message in messages arr if message belongs to this chat.
  // Update chats arr for update last message in this chat
  onMessageEvent(msg) {
    console.log('Chat: onMessage', msg);
    // console.log('Message with contact: ', msg);
    if (t.account.id === msg.ownerId) store.dispatch('messenger/getChats');
    if (this.chat.id === msg.roomId) this.addMessage(msg, 'simple');
  }

  // On accept event
  // Update chats arr for show accepted chat in chat list
  onAcceptedEvent() {
    console.log('Chat: onAccepted');
    this.update();
    if (t.account.role === 'PATIENT') {
      t.socket.emit('join-room', { roomId: this.chat.id });
      t.socket.emit('leave', { roomId: this.chat.id });
      this.removeMessage('system');
    }
  }

  // On rejected event
  onRejectedEvent() {
    console.log('Chat: onRejected');
    this.update();

    if (t.account.role === 'PATIENT') {
      this.removeMessage('system');
      if (t.account.role === 'PATIENT') this.addMessage(SYSTEM_MESSAGES.SYSTEM_MESSAGE_REJECTED, 'system');
      else if (t.account.role === 'CLINIC_ADMIN') this.addMessage(SYSTEM_MESSAGES.CA_SYSTEM_MESSAGE_REJECTED, 'system');
    }
  }

  // On disable patient event
  onDisablePatientEvent() {
    console.log('Chat: onDisabled');
    this.update();
    this.removeMessage('system');
    this.addMessage(SYSTEM_MESSAGES.SYSTEM_MESSAGE_DISABLED, 'system');
  }

  // On remove clinic admin event
  onRemovedClinicAdminEvent() {
    console.log('Chat: onRemovedClinicAdmin');
    this.update();
  }

  // On leave event
  onLeaveEvent(event) {
    console.log('Chat: onLeave', event);
    t.socket.emit('leave', { roomId: this.chat.id });
    this.update(true);
  }
}
